/**
 * Google Analytics Event Tracking Utilities
 * 
 * 자주 사용되는 이벤트 추적 함수들의 모음입니다.
 * 일관된 이벤트 명명 규칙과 파라미터를 유지하기 위해 사용합니다.
 */

import trackEvent from "~/core/lib/analytics.client";

// ============================================
// 🖱️ 사용자 인터랙션 추적
// ============================================

/**
 * 버튼 클릭 추적
 * 
 * @example
 * ```tsx
 * <button onClick={() => trackButtonClick('signup', 'hero_section')}>
 *   회원가입
 * </button>
 * ```
 */
export function trackButtonClick(buttonName: string, location?: string) {
  trackEvent("button_click", {
    button_name: buttonName,
    button_location: location,
    timestamp: new Date().toISOString(),
  });
}

/**
 * 링크 클릭 추적
 * 
 * @example
 * ```tsx
 * <a href="/pricing" onClick={() => trackLinkClick('/pricing', 'navigation')}>
 *   요금제
 * </a>
 * ```
 */
export function trackLinkClick(url: string, linkText?: string) {
  trackEvent("link_click", {
    link_url: url,
    link_text: linkText,
    timestamp: new Date().toISOString(),
  });
}

/**
 * 폼 제출 추적
 * 
 * @example
 * ```tsx
 * <form onSubmit={(e) => {
 *   e.preventDefault();
 *   trackFormSubmit('contact_form', true);
 * }}>
 * ```
 */
export function trackFormSubmit(formName: string, isSuccess: boolean) {
  trackEvent("form_submit", {
    form_name: formName,
    success: isSuccess,
    timestamp: new Date().toISOString(),
  });
}

// ============================================
// 🎯 전환(Conversion) 추적
// ============================================

/**
 * 회원가입 전환 추적
 * 
 * @example
 * ```tsx
 * async function handleSignup(email: string) {
 *   const result = await signUp(email);
 *   if (result.success) {
 *     trackSignup('email', 'free');
 *   }
 * }
 * ```
 */
export function trackSignup(method: string, plan?: string) {
  trackEvent("sign_up", {
    method, // 'email', 'google', 'github' etc.
    plan, // 'free', 'pro', 'enterprise' etc.
    timestamp: new Date().toISOString(),
  });
}

/**
 * 로그인 추적
 */
export function trackLogin(method: string) {
  trackEvent("login", {
    method, // 'email', 'google', 'github' etc.
    timestamp: new Date().toISOString(),
  });
}

/**
 * 결제 시작 추적
 * 
 * @example
 * ```tsx
 * function handleCheckout(plan: string, price: number) {
 *   trackPurchaseStart(plan, price, 'USD');
 *   // ... 결제 로직
 * }
 * ```
 */
export function trackPurchaseStart(
  productName: string,
  value: number,
  currency: string = "USD"
) {
  trackEvent("begin_checkout", {
    currency,
    value,
    items: [
      {
        item_name: productName,
      },
    ],
  });
}

/**
 * 결제 완료 추적
 * 
 * GA4의 표준 purchase 이벤트를 사용합니다.
 */
export function trackPurchaseComplete(
  transactionId: string,
  productName: string,
  value: number,
  currency: string = "USD"
) {
  trackEvent("purchase", {
    transaction_id: transactionId,
    currency,
    value,
    items: [
      {
        item_name: productName,
        price: value,
      },
    ],
  });
}

// ============================================
// 🤖 AI 기능 사용 추적
// ============================================

/**
 * AI 대화 시작 추적
 */
export function trackAIConversationStart(featureName: string) {
  trackEvent("ai_conversation_start", {
    feature_name: featureName, // 'explain', 'question', 'brainstorm' etc.
    timestamp: new Date().toISOString(),
  });
}

/**
 * AI 메시지 전송 추적
 */
export function trackAIMessageSent(featureName: string, messageLength: number) {
  trackEvent("ai_message_sent", {
    feature_name: featureName,
    message_length: messageLength,
    timestamp: new Date().toISOString(),
  });
}

/**
 * AI 응답 수신 추적
 */
export function trackAIResponseReceived(
  featureName: string,
  responseLength: number,
  responseTime: number // milliseconds
) {
  trackEvent("ai_response_received", {
    feature_name: featureName,
    response_length: responseLength,
    response_time_ms: responseTime,
    timestamp: new Date().toISOString(),
  });
}

// ============================================
// 📈 사용자 참여도 추적
// ============================================

/**
 * 검색 수행 추적
 */
export function trackSearch(searchTerm: string, resultCount?: number) {
  trackEvent("search", {
    search_term: searchTerm,
    result_count: resultCount,
    timestamp: new Date().toISOString(),
  });
}

/**
 * 파일 다운로드 추적
 */
export function trackFileDownload(fileName: string, fileType: string) {
  trackEvent("file_download", {
    file_name: fileName,
    file_type: fileType,
    timestamp: new Date().toISOString(),
  });
}

/**
 * 비디오 재생 추적
 */
export function trackVideoPlay(videoTitle: string, videoDuration?: number) {
  trackEvent("video_start", {
    video_title: videoTitle,
    video_duration: videoDuration,
    timestamp: new Date().toISOString(),
  });
}

/**
 * 공유 버튼 클릭 추적
 */
export function trackShare(
  contentType: string,
  method: string // 'facebook', 'twitter', 'link', etc.
) {
  trackEvent("share", {
    content_type: contentType,
    method,
    timestamp: new Date().toISOString(),
  });
}

// ============================================
// ⚠️ 에러 추적 (Sentry와 별개로)
// ============================================

/**
 * 사용자 경험 에러 추적
 * 
 * 기술적 에러는 Sentry에, 사용자 경험 관련 에러는 Analytics에 기록
 */
export function trackUserError(
  errorType: string,
  errorMessage: string,
  location: string
) {
  trackEvent("user_error", {
    error_type: errorType,
    error_message: errorMessage,
    error_location: location,
    timestamp: new Date().toISOString(),
  });
}

// ============================================
// 🎨 커스텀 이벤트 (프로젝트별)
// ============================================

/**
 * 문제 신고 버튼 클릭 추적
 */
export function trackFeedbackButtonClick(location: string) {
  trackEvent("feedback_button_click", {
    location,
    timestamp: new Date().toISOString(),
  });
}

/**
 * 피드백 제출 완료 추적
 */
export function trackFeedbackSubmitted(
  feedbackLength: number,
  hasContact: boolean
) {
  trackEvent("feedback_submitted", {
    feedback_length: feedbackLength,
    has_contact: hasContact,
    timestamp: new Date().toISOString(),
  });
}

